import { V2, hoverOnly } from './theme/tokens'
// src/CalendarView.jsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { addDays, format, isSameDay, startOfWeek } from 'date-fns';
import { berlinDateISO, formatDate, formatQty, formatShortDate, formatWeekday } from './utils/format';
import ActionSheet from './components/common/ActionSheet';

/**
 * CalendarView — compact weekly strip (Mon–Sun).
 *
 * Props:
 *   incomingEvents = Array<{ date: Date, resource: { … } }>
 *   reorderEvents  = Array<{ date: Date, resource: { … } }>
 *   onIncomingClick(resource, newDate?) → void
 *   onReorderClick(resource, newDate?)  → void
 *
 * Drag behaviour:
 *   • Clicking a pill → onIncomingClick(res, null) / onReorderClick(res, null)
 *   • Dragging an incoming pill to another day → onIncomingClick(res, targetDate)
 *
 * 🔴 Grober Zeiger (M5-18, UI-Durchgang 2 Teil 5): HTML5-Ziehen ist auf einem
 * Telefon nicht bedienbar — die Flaeche SIEHT bedienbar aus und ist es nicht.
 * Deshalb traegt die Liefer-Pille dort kein `draggable` mehr; der Tipp oeffnet
 * stattdessen ein Aktionsblatt (`components/common/ActionSheet`) mit ZWEI
 * Wegen. Der zweite ist kein Zusatz: `onIncomingClick(res, null)` NAVIGIERT
 * (der Kalender ist der einzige Einstieg in den Kalender-PO), und ein Tipp, der
 * nur den Datumswaehler oeffnete, naehme diesen Rueckweg weg (Grill B4, G11).
 *   • „Open PO"    → onIncomingClick(res, null)  — der heutige Weg, unveraendert
 *   • „Change Date"→ Datumswaehler, dann onIncomingClick(res, datum)
 * Die Achse ist `(pointer: coarse)` — dieselbe wie der Touch-Boden
 * (`theme/tokens.js:355-364`, Entscheid F1), NICHT `any-pointer`.
 *
 * 🔴 M7-Nachbesserung 1 (2026-10-09), zwei Befunde am Bild des Blattes:
 *   1. Das Blatt sagte nicht, WELCHE Lieferung. Auf dem Schreibtisch traegt die
 *      Pille ihre Angaben im Tooltip; auf einem groben Zeiger ist dieser Tooltip
 *      ABGESCHALTET (`disableTouchListener`, Nachbesserung 2 Punkt 2) — bei zwei
 *      Pillen am selben Tag war nach dem Tipp nicht entscheidbar, welche man
 *      erwischt hat. Deshalb steht in BEIDEN Schritten eine Kontextzeile
 *      oberhalb der Bedienelemente (`kontextZeile`).
 *   2. Der Datumsschritt startete LEER. Die Lieferung HAT eine ETA; wer sie
 *      verschiebt, geht von ihr aus. Sie ist jetzt vorbelegt, und „Save" ist
 *      erst aktiv, wenn der Wert davon ABWEICHT (`datumGeaendert`).
 *
 * 🔴 M7-Nachbesserung 2 (2026-10-09), die Auflagen der beiden Gates (Regel 15
 * B1/W2/W3, Regel 16 C1/C2/W1/W3/W4/W5/W6):
 *   1. Die Vorbelegung las den UTC-Tag (`eta.slice(0, 10)`), die Kontextzeile
 *      daneben den Berliner Tag — fuer jede ETA zwischen 22:00 und 24:00 UTC
 *      standen zwei verschiedene Tage in EINEM Blatt. Sie laeuft jetzt ueber
 *      `berlinDateISO` (`etaAlsFeldwert`).
 *   2. Auf einem groben Zeiger oeffnete ein LANGSAMER Tipp den Tooltip (MUI
 *      `enterTouchDelay`, Vorgabe 700 ms) — mit einem gefuellten
 *      „Open PO"-Knopf darin, der auf keinem Geraet etwas tut (der Popper traegt
 *      `pointer-events: none`). Der Tooltip ist dort jetzt abgeschaltet.
 *   3. Die Pille war kein Bedienelement: kein `role`, kein `tabIndex`, kein
 *      Tastenweg. Auf einem groben Zeiger ist sie der EINZIGE Eingang zu beiden
 *      Wegen des Blattes.
 *   4. Die Eintraege des Ueberlauf-Dialogs („+N more") kannten die neue Handlung
 *      nicht — die vierte Lieferung eines Tages war davon ausgeschlossen.
 *   5. Aus dem Datumsschritt fuehrte kein Weg zurueck, und die Beschriftungen
 *      des Blattes sprachen zwei Schreibweisen.
 */
export default function CalendarView({
  incomingEvents = [],
  reorderEvents = [],
  onIncomingClick,
  onReorderClick,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // ── State ─────────────────────────────────────────────────────────────────
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [dragOverDate, setDragOverDate] = useState(null);
  const [dialogItems, setDialogItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  // ── Grober Zeiger + Aktionsblatt der Liefer-Pille (M5-18) ─────────────────
  const groberZeiger = useMediaQuery('(pointer: coarse)');
  // EIN Zustand fuer beide Schritte des Blattes — `schritt: 'wahl' | 'datum'`.
  // Zwei Blaetter waeren zwei Flaechen mit zwei Uebergaengen; die Hausform
  // (`PODetail.jsx:3478-3500`) haelt je Handlung EIN Blatt.
  const [pilleAktion, setPilleAktion] = useState(null);
  const [pilleDatum, setPilleDatum] = useState('');

  /**
   * Die bisherige ETA der Lieferung als Feldwert `YYYY-MM-DD` — die Vorbelegung
   * des Datumsschritts (M7-Nachbesserung 1, Befund 2).
   *
   * 🔴 WARUM VORBELEGT. Die Lieferung HAT eine ETA (`IncomingInventory.jsx:299`
   * baut den Termin daraus). Wer sie verschiebt, will von ihr ausgehen und nicht
   * ein vollstaendiges Datum auf einer Telefontastatur tippen.
   *
   * Der Wert wird als BERLINER Kalendertag gelesen, ueber die Hausfunktion
   * `berlinDateISO` (`utils/time.js:192`, re-exportiert von `utils/format`).
   *
   * 🔴 WARUM NICHT `eta.slice(0, 10)` (M7-Nachbesserung 2, Regel-15-Befund B1).
   * `eta` ist ein ZEITSTEMPEL, keine Tagesangabe: `products_api.py:2476`
   * serialisiert `expected_arrival` ohne Offset (`'2026-11-03T23:41:12'`), und
   * `purchase_orders/service.py:86` schreibt die Uhrzeit der Statusaenderung mit
   * (`start + timedelta(days=total_days)`) — der Bereich 22:00–24:00 UTC ist also
   * ueber den normalen Weg „Order placed" erreichbar. Ein `slice(0, 10)` laese
   * darauf den UTC-Tag, und fuer alles in diesem Band ist der Berliner Tag der
   * NAECHSTE. Dann stuenden zwei verschiedene Tage in EINEM Blatt, weil die
   * Kontextzeile darueber `formatDate(resource.eta)` benutzt — und das ist
   * `formatBerlinDate` (`utils/format.js:75`), also Berlin. Genau diese Falle
   * nennt der Docstring von `berlinDateISO` woertlich; sie war hier der zweite
   * Fall.
   *
   * 🔴 DER RUECKWEG PASST DAZU. `datumAusFeld` setzt aus dem Feldwert LOKALE
   * Mitternacht, und `IncomingInventory.jsx:309` formatiert sie als
   * `yyyy-MM-dd` — also den lokalen Tag des gewaehlten Datums. Dieselbe Form
   * liefert der Zieh-Weg (`addDays(weekStart, i)`, ebenfalls lokale
   * Mitternacht). Ein `new Date('2026-11-04')` waere dagegen UTC-Mitternacht und
   * faellt in jeder Zeitzone westlich von Greenwich auf den Vortag. Vorbelegung
   * (Berliner Tag), Kontextzeile (Berliner Tag) und Schreibweg (lokaler Tag des
   * gewaehlten Datums) nennen damit denselben Tag.
   *
   * Ein fehlendes oder fremd geformtes `eta` ergibt den leeren Wert — also genau
   * das Verhalten von vorher (`berlinDateISO` gibt dafuer `null`).
   */
  const etaAlsFeldwert = (resource) => berlinDateISO(resource?.eta) || '';

  /**
   * Die Kontextzeile des Blattes — sie sagt, WELCHE Lieferung angetippt wurde
   * (M7-Nachbesserung 1, Befund 1).
   *
   * Auf dem Schreibtisch traegt die Pille ihre Angaben im Tooltip
   * (`tooltipContent` unten: Lieferant, Produkt, Variante, „On Hand"). Auf einem
   * groben Zeiger ist dieser Tooltip ABGESCHALTET (`disableTouchListener`,
   * M7-Nachbesserung 2 Punkt 2): ein langer Druck oeffnete ihn sonst nach
   * 700 ms samt eines „Open PO"-Knopfes, der nichts tut; wer zwei Pillen am
   * selben Tag hat, wuesste nach dem Tipp nicht, welche er erwischt hat.
   * Dieselben Felder, ergaenzt um die Menge aus dem Kreis der Pille und um die
   * ETA — die zwei Werte, um die es in diesem Blatt geht.
   *
   * 🔴 Fehlende Felder FALLEN WEG, keines wird zu „undefined". Eine kuerzere
   * Zeile ist richtig, eine Zeile mit Luecke ist falsch.
   *
   * Das Datum laeuft ueber die Hausfunktion (`utils/format`) — dieselbe, die die
   * Tabelle der Seite benutzt (`IncomingInventory.jsx:564`).
   */
  const kontextZeile = (resource) => {
    if (!resource) return '';
    const teile = [];
    if (resource.vendor_name) teile.push(`Vendor: ${resource.vendor_name}`);
    const ware = [resource.product_name, resource.variant_name].filter(Boolean).join(' – ');
    if (ware) teile.push(`Product: ${ware}`);
    const hatWert = (v) => v !== null && v !== undefined && v !== '';
    if (hatWert(resource.inbound_qty)) teile.push(`Qty: ${formatQty(resource.inbound_qty)}`);
    if (resource.eta) teile.push(`ETA: ${formatDate(resource.eta)}`);
    if (hatWert(resource.current_on_hand)) {
      teile.push(`On Hand: ${formatQty(resource.current_on_hand)}`);
    }
    return teile.join(' · ');
  };

  const oeffnePille = (resource) => {
    setPilleDatum(etaAlsFeldwert(resource));
    setPilleAktion({ resource, schritt: 'wahl' });
  };
  const schliessePille = () => setPilleAktion(null);
  const waehleDatum = () => setPilleAktion((p) => (p ? { ...p, schritt: 'datum' } : p));
  // 🔴 Der Rueckweg aus dem Datumsschritt (M7-Nachbesserung 2, Regel-16-Befund
  // W4). Ohne ihn kam man nur ueber „Cancel" zurueck — das Blatt zu —, und weil
  // der Titel dabei wechselt, sah der zweite Schritt wie ein ANDERES Blatt aus:
  // die zwei Wege des ersten wirkten verloren. Der Zustand ist derselbe wie in
  // `waehleDatum`, nur in die andere Richtung; das gewaehlte Datum bleibt
  // stehen, `oeffnePille` setzt es beim naechsten Oeffnen ohnehin neu.
  const zurueckZurWahl = () => setPilleAktion((p) => (p ? { ...p, schritt: 'wahl' } : p));
  const imDatumsSchritt = pilleAktion?.schritt === 'datum';
  // 🔴 „Save" ist erst aktiv, wenn das Datum vom VORBELEGTEN abweicht. Sonst
  // fuehrte ein Tipp ohne Aenderung einen Schreibweg aus — ein PUT auf dieselbe
  // Ankunft —, und die Zusage „ein gewaehltes Datum verschiebt die Ankunft"
  // waere nicht mehr messbar, weil derselbe Wert zweimal gesendet wuerde.
  const datumGeaendert = !!pilleDatum && pilleDatum !== etaAlsFeldwert(pilleAktion?.resource);

  /**
   * 🔴 Das Datumsfeld liefert `YYYY-MM-DD`; der Handler darunter erwartet ein
   * `Date` (er formatiert es selbst, `IncomingInventory.jsx:309`). `new
   * Date('2026-10-15')` liest den Wert als UTC-Mitternacht — in einer Zeitzone
   * westlich von Greenwich faellt das auf den VORTAG. Die Bestandteile werden
   * deshalb einzeln gelesen und als LOKALE Mitternacht gesetzt, genau die Form,
   * die auch der Zieh-Weg liefert (`days` → `addDays(weekStart, i)`).
   */
  const datumAusFeld = (wert) => {
    const [jahr, monat, tag] = wert.split('-').map(Number);
    return new Date(jahr, monat - 1, tag);
  };

  const pillePrimary = () => {
    if (!pilleAktion) return;
    onIncomingClick(
      pilleAktion.resource,
      imDatumsSchritt ? datumAusFeld(pilleDatum) : null
    );
    setPilleAktion(null);
  };

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = days[6];

  const prevWeek = () => setWeekStart((d) => addDays(d, -7));
  const nextWeek = () => setWeekStart((d) => addDays(d, 7));

  // ── Dedupe: never show same SKU twice on the same day ─────────────────────
  const dedupe = (events, type) => {
    const m = new Map();
    events.forEach((e) => {
      const key = `${e.date.valueOf()}|${e.resource.sku}`;
      if (!m.has(key)) m.set(key, { date: e.date, resource: e.resource, type });
    });
    return Array.from(m.values());
  };
  const allIncoming = dedupe(incomingEvents, 'incoming');
  const allReorder  = dedupe(reorderEvents, 'reorder');

  // ── Dialog ────────────────────────────────────────────────────────────────
  const openDialog  = (items) => { setDialogItems(items); setDialogOpen(true); };
  const closeDialog = () => setDialogOpen(false);

  // ── Drag / drop handlers ──────────────────────────────────────────────────
  const handleDragOver = (e, dt) => {
    e.preventDefault();
    if (!dragOverDate || !isSameDay(dragOverDate, dt)) setDragOverDate(dt);
  };

  const handleDrop = (e, dt) => {
    e.preventDefault();
    setDragOverDate(null);
    try {
      const { resource, type } = JSON.parse(e.dataTransfer.getData('application/json'));
      if (type === 'incoming') onIncomingClick(resource, dt);
      else onReorderClick(resource, dt);
    } catch {
      // ignore parse errors
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  // 🔴 Vorher `06. Okt – 12. Okt 2026` (deutsche Monatsnamen über
  // `date-fns/locale/de`); jetzt numerisch — der Owner-Entscheid nennt für
  // Kalenderflächen dieselbe Form wie für die Anzeige.
  const weekLabel = `${formatShortDate(weekStart)} – ${formatDate(weekEnd)}`;

  return (
    <>
      {/* ── Week navigation ──
          🔴 Die Pfeile waren Textglyphen (`‹`/`›`) in einem `Button` mit
          `minWidth: 32` — rund 10 px sichtbar und damit kein Tippziel, und ohne
          Namen für einen Screenreader (Fotos, incoming-inventory Desktop:
          „Kalender-Pfeile ‚‹ ›' als Text-Glyphen ~10 px (Touch-Ziel)").
          `IconButton` traegt den Hausboden für grobe Zeiger
          (`TOUCH_TARGET_COARSE_QUADRAT`, `theme.js`), der Name kommt aus
          `aria-label`. */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <IconButton size="small" onClick={prevWeek} aria-label="Previous week">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2" fontWeight={600}>{weekLabel}</Typography>
        <IconButton size="small" onClick={nextWeek} aria-label="Next week">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── 7-day strip ── */}
      <Box
        display="flex"
        sx={{
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {days.map((dt, idx) => {
          const isToday     = isSameDay(dt, new Date());
          const isDragTarget = dragOverDate && isSameDay(dt, dragOverDate);
          const inEvents    = allIncoming.filter((e) => isSameDay(e.date, dt));
          const roEvents    = allReorder.filter((e) => isSameDay(e.date, dt));

          return (
            <Box
              key={idx}
              onDragOver={(e) => handleDragOver(e, dt)}
              onDrop={(e) => handleDrop(e, dt)}
              onDragLeave={() =>
                setDragOverDate((prev) => (prev && isSameDay(prev, dt) ? null : prev))
              }
              sx={{
                flex: 1,
                minHeight: 120,
                borderLeft: idx > 0 ? (t) => `1px solid ${t.palette.divider}` : 'none',
                bgcolor: isDragTarget
                  ? (isDark ? V2.ink : V2.infoSoft)
                  : isToday
                    ? 'action.hover'
                    : 'inherit',
                outline: isDragTarget ? `2px dashed ${theme.palette.primary.main}` : undefined,
                outlineOffset: '-2px',
                p: 0.75,
                overflowY: 'auto',
                transition: 'background-color 0.15s',
              }}
            >
              {/* ── Day label ── */}
              <Box sx={{ mb: 0.75, textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 9 }}
                >
                  {formatWeekday(dt)}
                </Typography>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    mx: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isToday ? 'primary.main' : 'transparent',
                    color: isToday ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11 }}>
                    {format(dt, 'd')}
                  </Typography>
                </Box>
              </Box>

              {/* ── Incoming pills (max 3) ── */}
              {inEvents.slice(0, 3).map((e, i) => (
                <EventPill
                  key={`in-${i}`}
                  theme={theme}
                  isDark={isDark}
                  event={e}
                  onClick={() =>
                    groberZeiger
                      ? oeffnePille(e.resource)
                      : onIncomingClick(e.resource, null)
                  }
                  onDragStart={
                    groberZeiger
                      ? null
                      : (ev) =>
                          ev.dataTransfer.setData(
                            'application/json',
                            JSON.stringify({ resource: e.resource, type: 'incoming' })
                          )
                  }
                  testId="calendar-incoming-pill"
                  // 🔴 Auf grobem Zeiger ist der Tooltip ABGESCHALTET
                  // (M7-Nachbesserung 2, Regel-16-Befund C1). MUI oeffnet ihn
                  // dort nach `enterTouchDelay` (Vorgabe 700 ms), und darin
                  // steht ein gefuellter „Open PO"-Knopf OHNE `onClick` — der
                  // Popper traegt `pointer-events: none`, der Knopf tut also auf
                  // jedem Geraet nichts. Ein langsamer Tipp haette damit genau
                  // die Fehlerklasse erzeugt, gegen die M7 antritt: eine Flaeche,
                  // die bedienbar aussieht und es nicht ist. Der Inhalt geht
                  // nicht verloren — Lieferant, Ware, Menge, ETA und Bestand
                  // stehen in der Kontextzeile des Blattes (`kontextZeile`).
                  disableTouchListener={groberZeiger}
                  onDragEnd={() => setDragOverDate(null)}
                  tooltipContent={
                    <>
                      <Typography noWrap><strong>Vendor:</strong> {e.resource.vendor_name}</Typography>
                      <Typography noWrap><strong>Product:</strong> {e.resource.product_name}</Typography>
                      <Typography noWrap><strong>Variant:</strong> {e.resource.variant_name}</Typography>
                      <Typography><strong>On Hand:</strong> {e.resource.current_on_hand}</Typography>
                      <Box mt={1}>
                        <Button size="small" variant="contained" color="primary">Open PO</Button>
                      </Box>
                    </>
                  }
                  qty={e.resource.inbound_qty}
                  bg={theme.palette.primary.dark}
                  circleBg={isDark ? theme.palette.primary.light : theme.palette.primary.main}
                  text={`${e.resource.product_name} – ${e.resource.variant_name}`}
                  vendor={e.resource.vendor_name}
                />
              ))}
              {inEvents.length > 3 && (
                <Typography
                  sx={{ fontSize: 10, color: 'text.secondary', cursor: 'pointer', mt: 0.25 }}
                  onClick={() => openDialog(inEvents)}
                >
                  +{inEvents.length - 3} more
                </Typography>
              )}

              {/* ── Reorder pills (max 3) ── */}
              {roEvents.slice(0, 3).map((e, i) => (
                <EventPill
                  key={`ro-${i}`}
                  theme={theme}
                  isDark={isDark}
                  event={e}
                  onClick={() => onReorderClick(e.resource, null)}
                  onDragStart={null}
                  onDragEnd={() => setDragOverDate(null)}
                  testId="calendar-reorder-pill"
                  tooltipContent={
                    <>
                      <Typography noWrap><strong>Vendor:</strong> {e.resource.vendor_name}</Typography>
                      <Typography noWrap><strong>Product:</strong> {e.resource.product_name}</Typography>
                      <Typography noWrap><strong>Variant:</strong> {e.resource.variant_name}</Typography>
                      <Typography><strong>Qty:</strong> {e.resource.recommended_order_qty}</Typography>
                      <Box mt={1}>
                        <Button size="small" variant="contained" color="secondary">New reorder</Button>
                      </Box>
                    </>
                  }
                  qty={e.resource.recommended_order_qty}
                  bg={theme.palette.secondary.dark}
                  circleBg={isDark ? theme.palette.secondary.light : theme.palette.secondary.main}
                  text={`${e.resource.product_name} – ${e.resource.variant_name}`}
                  vendor={e.resource.vendor_name}
                />
              ))}
              {roEvents.length > 3 && (
                <Typography
                  sx={{ fontSize: 10, color: 'text.secondary', cursor: 'pointer', mt: 0.25 }}
                  onClick={() => openDialog(roEvents)}
                >
                  +{roEvents.length - 3} more
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ── Overflow dialog ── */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Items for selected day</DialogTitle>
        <DialogContent dividers>
          <List>
            {dialogItems.map((e, i) => (
              <ListItem
                key={i}
                button
                onClick={() => {
                  closeDialog();
                  if (e.type === 'incoming') {
                    // 🔴 Auf einem groben Zeiger fuehrt der Eintrag in DASSELBE
                    // Blatt wie die Pille (M7-Nachbesserung 2, Regel-16-Befund
                    // W3). Vorher rief er `onIncomingClick(res, null)`, also nur
                    // den Navigationsweg — die vierte Lieferung eines Tages war
                    // damit von der neuen Handlung ausgeschlossen, und der
                    // Ueberlauf ist eine ZWEITE Flaeche derselben Liste. Der Weg
                    // zur Bestellung geht nicht verloren: das Blatt traegt
                    // „Open PO".
                    if (groberZeiger) oeffnePille(e.resource);
                    else onIncomingClick(e.resource, null);
                  } else onReorderClick(e.resource, null);
                }}
              >
                <ListItemText
                  primary={`${e.resource.vendor_name}: ${e.resource.product_name} – ${e.resource.variant_name}`}
                  secondary={
                    e.type === 'incoming'
                      ? `On Hand: ${e.resource.current_on_hand}`
                      : `Qty: ${e.resource.recommended_order_qty}`
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Aktionsblatt der Liefer-Pille — der Ersatz fuer das Ziehen ──
          Nur auf grobem Zeiger erreichbar (`oeffnePille` haengt an
          `groberZeiger`). Titel und Primaerknopf wechseln mit dem Schritt:
          „wahl" traegt beide Wege, „datum" den Datumswaehler.

          🔴 Der Datumswaehler ist die HAUSFORM (natives `type="date"`,
          `PODetail.jsx:3489-3499`) — kein neuer Baustein. Das `lang` ist eine
          BITTE an Chromium, keine Zusage; die Anzeige folgt der
          Browser-/Systemsprache (`components/POStatusBar.jsx:150-153`). */}
      <ActionSheet
        open={!!pilleAktion}
        onClose={schliessePille}
        // 🔴 Der Titel des ersten Schritts (M7-Nachbesserung 2, Regel-16-Befund
        // W6). „Delivery" nannte keine Handlung; die Hausform der Nachbarschaft
        // benennt sie („Change Currency Rate", „Set Actual Arrival Date") — hier
        // gibt es aber ZWEI Wege, ein Titel, der einen davon nennt, waere falsch.
        // Deshalb die Frage. Im Datumsschritt nennt der Titel die Handlung.
        title={imDatumsSchritt ? 'Change Delivery Date' : 'What do you want to do?'}
        // 🔴 Titelform fuer beide Knoepfe (Regel-16-Befund W5): „Change date"
        // stand in Satzschreibung neben „Open PO" und „Save". Satzschreibung ist
        // im Haus die Norm fuer BESCHRIFTUNGEN, Titelform fuer Aktionsknoepfe.
        primaryLabel={imDatumsSchritt ? 'Save' : 'Open PO'}
        primaryDisabled={imDatumsSchritt && !datumGeaendert}
        onPrimary={pillePrimary}
      >
        {/* Die Kontextzeile steht in BEIDEN Schritten oberhalb der
            Bedienelemente — sie beantwortet die Frage, die das Blatt offen
            liess: welche Lieferung ist das? Sie ist Teil von `children`, kein
            neuer Baustein-Prop am `ActionSheet` (M7-Nachbesserung 1). */}
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {kontextZeile(pilleAktion?.resource)}
        </Typography>
        {imDatumsSchritt ? (
          <>
            {/* 🔴 „ETA", nicht „Arrival Date" (Regel-16-Befund C2): die Seite
                nennt die Groesse ueberall ETA (`IncomingInventory.jsx:881`
                `label="Next ETA"`, `:1005` `ETA: …`) und die Kontextzeile
                unmittelbar darueber ebenso. „Arrival Date" entlehnt ein Wort von
                der Nachbarseite `PODetail.jsx:3491`, wo es einen ANDEREN Vorgang
                beschriftet — dort die TATSAECHLICHE Ankunft
                (`actual_arrival_date`), hier die voraussichtliche
                (`expected_arrival`). Zwei Woerter, zwei Bedeutungen. */}
            <TextField
              type="date"
              label="ETA"
              size="small"
              fullWidth
              inputProps={{ lang: 'de-DE' }}
              InputLabelProps={{ shrink: true }}
              value={pilleDatum}
              onChange={(e) => setPilleDatum(e.target.value)}
              autoFocus
            />
            {/* Der Rueckweg (Befund W4) — ein Textknopf, kein zweites Blatt. Er
                sitzt in `children` und nicht in der Knopfleiste: `ActionSheet`
                kennt nur EINEN Primaerknopf, und der Baustein gehoert M2, nicht
                diesem Schritt (Dateigrenze). */}
            <Button
              variant="text"
              size="small"
              startIcon={<ChevronLeftIcon />}
              onClick={zurueckZurWahl}
              sx={{ mt: 1 }}
            >
              Back
            </Button>
          </>
        ) : (
          <Button variant="outlined" fullWidth onClick={waehleDatum}>
            Change date
          </Button>
        )}
      </ActionSheet>
    </>
  );
}

// ── EventPill ──────────────────────────────────────────────────────────────
// `testId` ist der Griff fuer die Proben (Jest `CalendarView.mobil.test.jsx`,
// Playwright `tests/incoming-inventory/kalender-mobil.spec.ts`): die Pille ist
// ueber ihren Inhalt nicht stabil adressierbar, ihre Texte kommen aus der
// Datenbank. `draggable` haengt weiterhin allein an `onDragStart` — der grobe
// Zeiger schaltet es ab, indem der Aufrufer `null` uebergibt.
//
// 🔴 DIE PILLE IST EIN BEDIENELEMENT (M7-Nachbesserung 2, Regel-16-Befund W1).
// Bis dahin war sie ein `<div>` mit `onClick`: kein `role`, kein `tabIndex`,
// kein Tastenweg, kein zugaenglicher Name — WCAG 2.1 **4.1.2** (Name, Rolle,
// Wert) war nicht erfuellt, und ein Bildschirmleser kuendigte sie nicht als
// betaetigbar an. Vorbestehend, aber M7 macht sie auf einem groben Zeiger zum
// EINZIGEN Eingang zu beiden Wegen des Blattes; die Hausform kennt sie daneben
// laengst (`StatTile` traegt `role="button"` + `tabIndex` + Enter/Leer,
// `component-registry.md:84`). `onKeyDown` fuehrt denselben Handler aus wie der
// Tipp — ein Tastenweg, der ein ANDERES Blatt oeffnete, waere schlimmer als
// keiner.
//
// `disableTouchListener` wird durchgereicht statt hier entschieden: welche
// Pille ihren Tooltip auf einem groben Zeiger behaelt, weiss nur der Aufrufer.
//
// 🔴 DER GRUND DER ASYMMETRIE (Regel-15-Warnung W1, 2026-10-09) — sie ist
// ABSICHT und keine Nachlaessigkeit.
//
// Bei der LIEFER-Pille geht nichts verloren, wenn ihr Tooltip verschwindet: sie
// hat ein Blatt, und dessen Kontextzeile traegt Lieferant, Ware, Variante,
// Menge, ETA und Bestand (`kontextZeile`). Der Tooltip ist dort die ZWEITE
// Flaeche fuer dieselben Angaben — und auf einem groben Zeiger die einzige, die
// man nur durch versehentliches Halten erreicht.
//
// Bei der UMLAGER-Pille gibt es KEIN Blatt. Ihr Tooltip ist in der Tagesspalte die
// einzige Detailflaeche (Lieferant, Produkt, Variante, `Qty`) ausserhalb des
// Ueberlauf-Dialogs; die Zahl steht ausserdem im Mengenkreis und im `aria-label`.
// Wer ihr den Tooltip nimmt, nimmt dem sehenden Nutzer diese Detailflaeche.
//
// ⚠️ Unberuehrt davon: die zwei toten Knoepfe in BEIDEN Tooltips („Open PO",
// „New Reorder", ohne `onClick`, im Popper mit `pointer-events: none`). Sie
// stehen als eigener Befund in `docs/backlog-auto-repair/2026-10-09-m7-folgearbeiten.md`
// §(d).
function EventPill({ theme, isDark, event, onClick, onDragStart, onDragEnd, tooltipContent, disableTouchListener = false, qty, bg, circleBg, text, vendor, testId }) {
  // Der zugaengliche Name. `aria-label` ERSETZT den Inhalt fuer den Namen —
  // deshalb stehen seine sichtbaren Teile mit darin (WCAG 2.5.3, „Label in
  // Name"): Lieferant, Ware und Menge. Der Tag kommt ueber `format` (date-fns)
  // und nicht ueber die Berlin-Formatierer: `event.date` ist ein
  // Kalendertag (lokale Mitternacht aus `addDays`), kein Zeitpunkt — eine
  // Umrechnung koennte ihn einen Tag verschieben.
  const hatQty = qty !== null && qty !== undefined && qty !== '';
  const beschriftung = [
    vendor,
    text,
    hatQty ? `Qty ${qty}` : null,
    event?.date ? format(event.date, 'dd.MM.yyyy') : null,
  ].filter(Boolean).join(' · ');

  return (
    <Tooltip title={tooltipContent} disableTouchListener={disableTouchListener}>
      <Box
        data-testid={testId}
        role="button"
        tabIndex={0}
        aria-label={beschriftung}
        draggable={!!onDragStart}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={() => onClick(event.resource, null)}
        onKeyDown={(ev) => {
          // Enter und Leer sind die zwei Tasten eines Knopfes. `preventDefault`
          // auf dem Leerzeichen: sonst scrollt die Seite darunter weg.
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            onClick(event.resource, null);
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: bg,
          color: theme.palette.getContrastText(bg),
          borderRadius: 1,
          px: 0.75,
          py: 0.5,
          mb: 0.5,
          cursor: 'pointer',
          // K-5 (Welle 2): eine reine WIRKUNG ohne Hintergrund — sie klebt auf
          // einem groben Zeiger genauso und gehoert hinter die Zeigerabfrage.
          ...hoverOnly({ opacity: 0.8 }),
        }}
      >
        <Typography sx={{ fontSize: 9, opacity: 0.8, mb: 0.25 }} noWrap>{vendor}</Typography>
        <Box
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: 10,
            fontWeight: 'bold',
            mb: 0.25,
          }}
          title={text}
        >
          {text}
        </Box>
        <Box
          sx={{
            alignSelf: 'flex-start',
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: circleBg,
            color: isDark ? theme.palette.primary.contrastText : '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 'bold',
          }}
        >
          {qty}
        </Box>
      </Box>
    </Tooltip>
  );
}
